import { useEffect, useRef } from 'react';
import { useFlagsAndLoadingState } from 'src/contexts/launch-darkly-flags-ready-context';

/**
 * Runs each callback when LaunchDarkly is ready and again when the `flags` snapshot changes.
 * Callbacks are kept on a ref so callers may pass a new object / inline functions each render
 * without retriggering the effect; the latest callbacks are used whenever it runs.
 */
export function useFeatureFlagsWhenReady(flagReadyCallbacks: Record<string, (isEnabled: boolean) => void>) {
  const { flagsReady, flags } = useFlagsAndLoadingState();
  const flagReadyCallbacksRef = useRef(flagReadyCallbacks);

  // Update to the latest callbacks on every render. This ensures that if the caller passes a new object / inline functions each render, the latest callbacks are used.
  flagReadyCallbacksRef.current = flagReadyCallbacks;

  useEffect(() => {
    if (!flagsReady) return;

    for (const [flag, callback] of Object.entries(flagReadyCallbacksRef.current)) {
      const isEnabled = Boolean(flags[flag]);
      callback(isEnabled);
    }
  }, [flagsReady, flags]);

  return { flagsReady, flags };
}
